<script setup lang="ts">
import { ref } from 'vue'
import { useToast } from 'primevue/usetoast'
import CustomButton from '@/components/CustomComponent/CustomButton.vue'
import type BalancesheetRequest from '@/api/models/reports/BalancesheetRequest'
import ApiService from '@/api/ApiService'
import type FinancialData from '@/api/models/reports/BalancesheetData'
import type { ToastMessageOptions } from 'primevue'
import LoadingComponent from '@/components/LoadingComponent.vue'
import ConversionUtility from '@/utils/ConversionUtility'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import router from '@/router'
import Storage from '@/utils/Storage'


const selectedYear = ref<number>()
const isLoading = ref<boolean>(false)
const toast = useToast()
const isDataExist = ref(false)

const financialYears = [
  { label: '2025-26', value: 2025 },
  { label: '2024-25', value: 2024 }
]

interface RowItem {
  liabilityLabel?: string
  amt?: string
  liabilityAmt?: string

  assetLabel?: string
  amtAsset?: string
  assetAmt?: string

  isChild?: boolean
  isChild1?: boolean
}

const rows = ref<RowItem[]>([])

const formatINR = (amount: number): string => {
  const rupees = ConversionUtility.toRuppesCoversion(amount)
  return rupees.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
}

const sampleData = ref<FinancialData>()

function transformToRowsWithTotals(financialData: FinancialData) {
  const result: RowItem[] = []

  const liabilities = financialData.liabilities.flatMap((group) => {
    const groupTotal = group.records.reduce((sum, rec) => sum + rec.value, 0)
    const rows: RowItem[] = []

    // Push group header
    rows.push({
      liabilityLabel: group.title,
      amt: '-',
      liabilityAmt: formatINR(groupTotal),
      isChild: false
    })

    // Push each child row
    group.records.forEach((record) => {
      rows.push({
        liabilityLabel: record.title,
        amt: formatINR(record.value),
        liabilityAmt: '-',
        isChild: true
      })
    })

    return rows
  })

  const assets = financialData.assets.flatMap((group) => {
    const groupTotal = group.records.reduce((sum, rec) => sum + rec.value, 0)
    const rows: RowItem[] = []

    // Push group header
    rows.push({
      assetLabel: group.title,
      amtAsset: '-',
      assetAmt: formatINR(groupTotal),
      isChild1: false
    })

    // Push each child row
    group.records.forEach((record) => {
      rows.push({
        assetLabel: record.title,
        amtAsset: formatINR(record.value),
        assetAmt: '-',
        isChild1: true
      })
    })

    return rows
  })

  const maxLength = Math.max(liabilities.length, assets.length)

  // Merge liabilities and assets into rows side by side
  for (let i = 0; i < maxLength; i++) {
    result.push({
      liabilityLabel: liabilities[i]?.liabilityLabel,
      amt: liabilities[i]?.amt,
      liabilityAmt: liabilities[i]?.liabilityAmt,
      isChild: liabilities[i]?.isChild,

      assetLabel: assets[i]?.assetLabel,
      amtAsset: assets[i]?.amtAsset,
      assetAmt: assets[i]?.assetAmt,
      isChild1: assets[i]?.isChild1
    })
  }

  // Add final total row
  const totalLiability = financialData.liabilities
    .flatMap((l) => l.records)
    .reduce((s, r) => s + r.value, 0)
  const totalAsset = financialData.assets.flatMap((a) => a.records).reduce((s, r) => s + r.value, 0)

  result.push({
    liabilityLabel: 'Total',
    liabilityAmt: formatINR(totalLiability),
    assetLabel: 'Total',
    assetAmt: formatINR(totalAsset)
  })

  rows.value = result
}

const fetchBalanceSheet = () => {
  if (!selectedYear.value) {
    showToast('Error', 'Please select Financial Year', 'error')
    return
  }
  const year = selectedYear.value

  const start = new Date(year, 3, 1, 0, 0, 0, 0)
  const end = new Date(year + 1, 2, 31, 23, 59, 59, 999)

  const request: BalancesheetRequest = {
    endDate: end.getTime().toString()
  }
  isLoading.value = true
  console.log(request)

  ApiService.getBalanceSheet(request)
    .then((response) => {
      setTimeout(() => {
        if (response.statusCode == 401) {
          Storage.clearData()
          router.push({ name: 'login' })
        }
      }, 2000)
      if (response.statusCode == 200) {
        console.log(response.data)
        sampleData.value = response.data
        transformToRowsWithTotals(sampleData.value)
        isDataExist.value = true
      }
    })
    .catch((error) => {
      console.log(error)
      showToast('Please try again!!!', 'Something is wrong', 'error')
    })
    .finally(() => {
      isLoading.value = false
    })
}

function generateBalanceSheetPDF(rows: RowItem[], selectedYear: number) {
  const doc = new jsPDF('l', 'mm', 'a4')
  let y = 15
  const endYear = selectedYear + 1
  const pageWidth = doc.internal.pageSize.width
  const centerX = pageWidth / 2

  // Header
  doc.setFontSize(24)
  doc.text('Bhavsar Patini Sahakar Sanga(R),', centerX, y, { align: 'center' })
  doc.setFontSize(14)
  doc.text('Badami, Tq:Badami, Dist:Bagalkot', centerX, y + 8, { align: 'center' })
  y += 20

  doc.setFontSize(18)
  doc.text('Balance Sheet', centerX, y, { align: 'center' })
  doc.setFontSize(12)
  doc.text(`As on 1st April ${selectedYear} to 31st March ${endYear}`, centerX, y + 7, {
    align: 'center'
  })
  y += 20

  // Prepare table body
  const body: any[] = rows.map((row) => [
    row.liabilityLabel ? (row.isChild ? '   ' + row.liabilityLabel : row.liabilityLabel) : '',
    row.amt || '',
    row.liabilityAmt || '',
    row.assetLabel ? (row.isChild1 ? '   ' + row.assetLabel : row.assetLabel) : '',
    row.amtAsset || '',
    row.assetAmt || ''
  ])

  // Calculate table width and center the table
  const tableWidth = 240 // Sum of all column widths
  const leftMargin = (pageWidth - tableWidth) / 2

  // Table
  autoTable(doc, {
    startY: y,
    head: [['Liabilities', '', 'Amount', 'Assets', '', 'Amount']],
    body,
    styles: {
      fontSize: 10,
      overflow: 'linebreak'
    },
    headStyles: {
      fillColor: '#5274B8',
      textColor: 'white',
      halign: 'center'
    },
    columnStyles: {
      0: { cellWidth: 60 },
      1: { cellWidth: 25, halign: 'right' },
      2: { cellWidth: 35, halign: 'right' }, 
      3: { cellWidth: 60 },
      4: { cellWidth: 25, halign: 'right' },
      5: { cellWidth: 35, halign: 'right' }
    },
    margin: { left: leftMargin, right: leftMargin },
    theme: 'grid',
    didParseCell(data) {
      const row = rows[data.row.index]

      if (!row) return
      if (data.column.index <= 2) {
        if (!row.isChild && row.liabilityLabel) {
          data.cell.styles.fontStyle = 'bold'
          data.cell.styles.textColor = [0, 0, 0]
        } else {
          data.cell.styles.fontStyle = 'normal'
          data.cell.styles.textColor = [80, 80, 80]
        }
      }

      // Apply style for Assets section
      if (data.column.index >= 3) {
        if (!row.isChild1 && row.assetLabel) {
          data.cell.styles.fontStyle = 'bold'
          data.cell.styles.textColor = [0, 0, 0]
        } else {
          data.cell.styles.fontStyle = 'normal'
          data.cell.styles.textColor = [80, 80, 80]
        }
      }
    }
  })

  // Add footer text
  const currentDate = new Date()
  const formattedDate = currentDate.toLocaleDateString('en-IN')
  const formattedTime = currentDate.toLocaleTimeString('en-IN')
  const footerText = `Report generated on: ${formattedDate} ${formattedTime}`
  
  // Get the total height of the document
  const pageHeight = doc.internal.pageSize.height
  
  // Position the footer text at the bottom of the page
  doc.setFontSize(8)
  doc.setTextColor(100, 100, 100) // Gray color
  doc.text(footerText, 15, pageHeight - 10) // Bottom left
  doc.text('Powered by Matrex Softwares', pageWidth - 15, pageHeight - 10, { align: 'right' }) // Bottom right
  
  doc.autoPrint()
  window.open(doc.output('bloburl'), '_blank')
  doc.save(`BalanceSheet_${selectedYear}-${endYear}.pdf`)
}

const handlePrint = () => {
  if (rows.value.length === 0 || !selectedYear.value) {
    showToast('Error', 'Please generate the balance sheet first.', 'error')
    return
  }
  generateBalanceSheetPDF(rows.value, selectedYear.value)
}

function showToast(summary: string, detail: string, severity: ToastMessageOptions['severity']) {
  toast.add({
    severity: severity,
    summary: summary,
    detail: detail,
    life: 5000
  })
}
</script>

<template>
  <Toast class="error-p-toast" />
  <main class="main">
    <Card class="search-card">
      <template #title>
        <h2 class="title-right text-3xl font-bold">Balancesheet</h2>
      </template>
      <template #content>
        <Divider />

        <div class="form-container">
          <div class="form-header">
            <i class="pi pi-calendar" style="font-size: 1.2rem;"></i>
            <span>Select a financial year to generate the balance sheet</span>
          </div>
          <div class="form-content">
            <div class="select-container">
              <FloatLabel>
                <Select
                  v-model="selectedYear"
                  id="selectedDate"
                  class="input-text"
                  :options="financialYears"
                  optionLabel="label"
                  optionValue="value"
                />
                <label for="selectedDate" class="mandatory">Financial Year</label>
              </FloatLabel>
            </div>
            <CustomButton title="Generate Report" @click="fetchBalanceSheet" class="generate-btn" icon="pi pi-file-pdf" />
          </div>
        </div>

        <div v-if="isDataExist">
          <h2 class="text-1xl font-bold text-center">Bhavsar Patini Sahakar Sanga(R), Badami</h2>
          <h5 class="text-center text-sm">Badami | Tq: Badami | Dist: Bagalkote</h5>
          <h2 class="p-4 text-3xl font-bold text-center">Balance Sheet</h2>
          <hr class="mb-5" />
        </div>

        <DataTable v-if="isDataExist && !isLoading" show-gridlines striped-rows :value="rows">
          <Column field="liabilityLabel" header="Liabilities">
            <template #body="{ data }">
              <div
                :style="{
                  paddingLeft: data.isChild ? '20px' : '0px',
                  fontWeight: data.isChild ? 'normal' : 'bold'
                }"
              >
                {{ data.liabilityLabel }}
              </div>
            </template>
          </Column>
          <Column>
            <template #body="{ data }">{{ data.amt }}</template>
          </Column>
          <Column field="liabilityAmt" header="Total" />

          <Column field="assetLabel" header="Assets">
            <template #body="{ data }">
              <div
                :style="{
                  paddingLeft: data.isChild1 ? '20px' : '0px',
                  fontWeight: data.isChild1 ? 'normal' : 'bold'
                }"
              >
                {{ data.assetLabel }}
              </div>
            </template>
          </Column>
          <Column>
            <template #body="{ data }">{{ data.amtAsset }}</template>
          </Column>
          <Column field="assetAmt" header="Total" />
        </DataTable>
        <div class="download-container" v-if="isDataExist">
          <CustomButton title="Download PDF" icon="pi pi-download" @click="handlePrint" class="download-btn" />
        </div>
      </template>
    </Card>
  </main>
  <div v-if="isLoading">
    <LoadingComponent
      text="Please wait while we process the request!"
      :dialogVisiblity="isLoading"
    />
  </div>
</template>

<style scoped>
/* Add your custom styling here */
.search-card {
  width: 100%;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  overflow: hidden;
}

.main {
  width: 100%;
  padding: 15px;
  max-width: 100%;
  margin: 0 auto;
  box-sizing: border-box;
}

.form-container {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  border: 1px solid #e9ecef;
}

.form-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
  color: #495057;
  font-weight: 500;
}

.form-content {
  display: flex;
  align-items: flex-end;
  gap: 20px;
}

.select-container {
  flex: 1;
  max-width: 300px;
}

.input-text {
  width: 100%;
}

.generate-btn {
  height: 42px;
  background-color: #4361ee;
  border-color: #4361ee;
}

.generate-btn:hover {
  background-color: #3a56d4;
  border-color: #3a56d4;
}

.download-container {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}

.download-btn {
  width: auto;
  height: 42px;
  background-color: #4895ef;
  border-color: #4895ef;
}

.download-btn:hover {
  background-color: #3a7bd5;
  border-color: #3a7bd5;
}

.mandatory::after {
  content: ' *';
  color: #f03e3e;
}

.p-datatable {
  width: 100%;
  margin-top: 20px;
}

/* Additional responsive styles */
@media screen and (max-width: 768px) {
  .form-content {
    flex-direction: column;
    align-items: stretch;
  }

  .select-container {
    max-width: 100%;
  }

  .generate-btn {
    width: 100%;
  }
}
</style>
